import type { BadgeSize, BadgeTone, BadgeType } from './Badge.types.js';
import s from './Badge.module.css';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import type { AllowedDisplayElements } from '../utils/html.js';
import { getPolymorphicProps } from '../utils/polymorphic.js';

const DEFAULT_ELEMENT = 'div' as const;

/**
 * Props specific to the Badge component
 */
type BadgeOwnProps<T extends AllowedDisplayElements = typeof DEFAULT_ELEMENT> =
  {
    children: ReactNode;
    size?: BadgeSize;
    type?: BadgeType;
    tone?: BadgeTone;
    as?: T;
  };

/**
 * Full Badge props combining own props with HTML element props (including ref)
 */
export type BadgeProps<
  T extends AllowedDisplayElements = typeof DEFAULT_ELEMENT,
> = BadgeOwnProps<T> & Omit<ComponentPropsWithRef<T>, keyof BadgeOwnProps<T>>;

export const badgeVariants = cva(s.badge, {
  variants: {
    size: {
      sm: s.smSize,
      md: s.mdSize,
      lg: s.lgSize,
    },
    tone: {
      subtle: s.subtle,
      default: s.default,
      strong: s.strong,
    },
    type: {
      neutral: s.neutral,
      highlight: s.highlight,
      info: s.info,
      positive: s.positive,
      alert: s.alert,
      negative: s.negative,
      rectangle: s.rectangle,
      circle: s.circle,
    },
    variant: {
      icon: s.iconVariant,
    },
  },
});

export const Badge = <
  T extends AllowedDisplayElements = typeof DEFAULT_ELEMENT,
>(
  props: BadgeProps<T>,
) => {
  const {
    children,
    size = 'sm',
    type = 'neutral',
    tone = 'default',
    as = DEFAULT_ELEMENT,
    className,
    ref,
    ...attrs
  } = props;

  const { Component, props: componentProps } = getPolymorphicProps(as, {
    ref,
    className: clsx(badgeVariants({ size, type, tone, className })),
    ...attrs,
    children,
  });

  return <Component {...componentProps} />;
};
